// components/CompanyAccesMapDialog.jsx
import { useEffect, useState, useMemo, forwardRef, useImperativeHandle, useCallback } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import TransferListTable from '../../../components/TransferListTable';
import { fleetApi } from '../../../api/axiosClient';
import { updateCompanyAccess } from '../../../stores/companyAccessStore';
import { useLanguage } from '../../../utils/i18n';

function idsFrom(items = []) {
  return items.map((i) => String(i?.id ?? i)).join(',');
}

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const CompanyAccesMapDialog = forwardRef(function CompanyAccesMapDialog({ userRow, onDirtyChange, onSaved }, ref) {
  const { t } = useLanguage();
  const userId = userRow?.id ?? userRow?.user_id ?? null;
  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const normalize = (row) => {
    const id = String(row.company_access ?? row.key ?? row.company_id ?? row.id ?? row.value ?? '');
    return {
      id,
      name: row.company_name ?? row.company ?? row.name ?? '',
      code: row.company_code ?? row.companyCode ?? '',
      business_type: row.company_business_type ?? row.companyBusinessType ?? row.business_type ?? '',
      phone: row.company_phone ?? row.companyPhone ?? row.phone ?? ''
    };
  };

  const fetchAssignList = useCallback(
    async (uid, limit = 50, offset = 0) => {
      if (uid == null) return;
      setLoading(true);
      setError(null);
      try {
        const url = `/assign-company-access/list?userId=${encodeURIComponent(uid)}&limit=${limit}&offset=${offset}`;
        const resp = await fleetApi.get(url + getTzParam());
        const payload = resp?.data ?? resp;
        const rows = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.result)
              ? payload.result
              : (payload?.rows ?? []);
        const items = rows.map(normalize).filter((it) => it.id);
        const assigned = [];
        const available = [];
        // mark assigned by checking selected flag or presence in rows.selected
        rows.forEach((r) => {
          const id = String(r.company_access ?? r.key ?? r.company_id ?? r.id ?? r.value ?? '');
          const isSelected = r.selected === true || (r.status ?? '').toString().toLowerCase() === 'assign' || Boolean(r.selected);
          if (isSelected) {
            const obj = items.find((it) => it.id === id) ?? normalize(r);
            assigned.push(obj);
          }
        });
        // any remaining items not in assigned go to available
        items.forEach((it) => {
          if (!assigned.some((a) => String(a.id) === String(it.id))) available.push(it);
        });
        setLeftItems((prev) => (idsFrom(prev) === idsFrom(available) ? prev : available));
        setRightItems((prev) => (idsFrom(prev) === idsFrom(assigned) ? prev : assigned));
        setDirty(false);
        if (typeof onDirtyChange === 'function') onDirtyChange(false);
      } catch (err) {
        setError(err?.message ?? t('masterUser.error1', 'Failed to retrieve data'));
      } finally {
        setLoading(false);
      }
    },
    [onDirtyChange, t]
  );

  useEffect(() => {
    if (userId == null) {
      setLeftItems([]);
      setRightItems([]);
      return;
    }
    fetchAssignList(userId, 1000, 0);
  }, [userId, fetchAssignList]);

  const companyColumns = useMemo(
    () => [
      { field: 'name', label: t('general.company', 'Company') },
      { field: 'code', label: t('masterUser.code', 'Code'), render: (it) => it.code ?? '-' },
      { field: 'business_type', label: t('masterUser.bussinesType', 'Business Type'), render: (it) => it.business_type ?? '-' },
      { field: 'phone', label: t('masterUser.phone', 'Phone'), render: (it) => it.phone ?? '-' }
    ],
    [t]
  );

  const handleChange = useCallback(
    ({ left = [], right = [] }) => {
      const leftObjs = (left || []).map((id) => {
        const found = leftItems.find((c) => String(c.id) === String(id)) || rightItems.find((c) => String(c.id) === String(id));
        return found ? { ...found } : { id: String(id), name: String(id) };
      });
      const rightObjs = (right || []).map((id) => {
        const found = rightItems.find((c) => String(c.id) === String(id)) || leftItems.find((c) => String(c.id) === String(id));
        return found ? { ...found } : { id: String(id), name: String(id) };
      });
      setLeftItems(leftObjs);
      setRightItems(rightObjs);
      setDirty(true);
      if (typeof onDirtyChange === 'function') onDirtyChange(true);
    },
    [leftItems, rightItems, onDirtyChange]
  );

  const save = useCallback(async () => {
    if (!userId) throw new Error('userId is required');
    setSaving(true);
    setError(null);
    try {
      const ids = (rightItems || []).map((c) => Number(c.id));
      await updateCompanyAccess(userId, ids);
      setDirty(false);
      if (typeof onDirtyChange === 'function') onDirtyChange(false);
      if (typeof onSaved === 'function') onSaved({ userId, assigned: ids });
      // refresh list from server to reflect authoritative state
      await fetchAssignList(userId, 1000, 0);
      return { success: true, assigned: ids };
    } catch (err) {
      setError(err?.message ?? t('masterUser.error2', 'Failed saving data'));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [userId, rightItems, onDirtyChange, onSaved, fetchAssignList, t]);

  useImperativeHandle(
    ref,
    () => ({
      save,
      isDirty: () => dirty,
      getAssignedIds: () => (rightItems || []).map((c) => String(c.id ?? c))
    }),
    [save, dirty, rightItems]
  );

  if (!userRow) {
    return (
      <Box>
        <Typography variant="body1">{t('masterUser.subtitle1', 'Select a user to view Company Access')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {loading || saving ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {error && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <TransferListTable
            leftItems={leftItems}
            rightItems={rightItems}
            onChange={handleChange}
            titleLeft={t('masterUser.avaibleCompanies', 'Available Companies')}
            titleRight={t('masterUser.choosenCompanies', 'Choosen Companies')}
            itemKey="id"
            itemLabel="name"
            showSearch
            columns={companyColumns}
          />
        </>
      )}
    </Box>
  );
});

export default CompanyAccesMapDialog;
